<template>
  <div class="action_mpt_box">
    <!-- 1. 紧凑型映射看板 -->
    <div class="mpt_mapping_board">
      <div class="board_inner">
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active">{{ currentKeyDisp }}</div>
        </div>

        <div class="mapping_flow">
          <div class="flow_icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="flow_label">MPT</div>
        </div>

        <div class="steps_preview_horizontal">
          <div v-for="(item, index) in mptData" :key="index" class="preview_dot" :class="{ active: item.KeyCode?.some((k) => k > 0) }"></div>
        </div>
      </div>
    </div>
    <!-- 2. 三列网格布局 (Steps Grid) -->
    <div class="mpt_steps_grid">
      <div v-for="(item, row) in mptData" :key="row" class="mpt_step_card">
        <div class="card_content">
          <trigger_btn class="key_slot target" :label="$t('mapping_action')" :vks="item.KeyCode || [0, 0, 0]" :tab="tab" :show-edit="true" :large="true" @trigger-click="onTriggerBtnClick(row)" />

          <div class="step_slider_group">
            <div class="slider_info">
              <div class="travel_display">{{ (travels[row] / travelconfig.unit).toFixed(travelconfig.decimalPlaces) }}<span>mm</span></div>
            </div>
            <div class="step_slider_container">
              <input
                class="slider_input"
                type="range"
                v-model.number="travels[row]"
                :max="pressMaxTravel()"
                :min="pressMinTravel()"
                :step="pressMaxTravel() / pressStep()"
                @change="changeTravel(row)"
                :style="{ backgroundSize: timeBackgroundSize(travels[row]) }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="selectedRow" :base-tab="tab" :selected-keys="mptData[selectedRow]?.KeyCode || [0, 0, 0]" @confirm="onKeyConfirm" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, toRefs, computed, watch } from 'vue'
import action_key_dialog from './action_key_dialog.vue'
import trigger_btn from './trigger_btn.vue'
import { SetMagnetKey } from '@libs/types/response'
import { actionSet, BaseTabs } from '@/service/driver/action_set'
import { VK, kks } from '@/define/define'
import { getTravelStep, travelConfig } from '@/define/magnet_travel'
import { DeviceManager } from '@/service/device'
import { MagnetSeniorType } from '@libs/types/enum'
import { normalizeKeyCode } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText, getVkText } from './action_config'

const props = defineProps<{ tab: BaseTabs; keys: VK[] }>()
const { KeyType, Keys } = toRefs(actionSet.magnetValue)
const travelconfig = ref(travelConfig(DeviceManager.instance.state.currentPid))
const travelStep = ref(100)
const selectedRow = ref(0)
const showKeyDialog = ref(false)

const currentKeyDisp = computed(() => currentSelectedKeyText.value)

const getTargetKeyName = (keyCodes: number[]) => {
  const code = keyCodes[2] || keyCodes[0] || 0
  return getVkText(code)
}

const MPTDefaultKeys = [
  { KeyCode: [0x00], KeyFunction: [0x00, 0x00, 0x00, 0x00], Travel: 500 },
  { KeyCode: [0x00], KeyFunction: [0x00, 0x00, 0x00, 0x00], Travel: 1000 },
  { KeyCode: [0x00], KeyFunction: [0x00, 0x00, 0x00, 0x00], Travel: 1500 },
]

const travels = ref([MPTDefaultKeys[0].Travel, MPTDefaultKeys[1].Travel, MPTDefaultKeys[2].Travel])
const mptData = ref<SetMagnetKey[]>(JSON.parse(JSON.stringify(MPTDefaultKeys)))

const initStep = async () => {
  travelStep.value = await getTravelStep()
}

const pressMinTravel = () => travelconfig.value.minTravel || pressMaxTravel() / travelStep.value
const pressMaxTravel = () => travelconfig.value.maxTravel
const pressStep = () => travelStep.value

const timeBackgroundSize = (pressTime: number) => {
  const timePercent = (pressTime / pressMaxTravel()) * 100
  return `${timePercent}%, 100%`
}

const onTriggerBtnClick = (row: number) => {
  selectedRow.value = row
  showKeyDialog.value = true
}

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (mptData.value[selectedRow.value]) {
    mptData.value[selectedRow.value].KeyCode = keys
  }
}

const changeTravel = (index: number) => {
  if (mptData.value[index]) {
    mptData.value[index].Travel = Number(travels.value[index])
  }
}

const initData = () => {
  // 准备默认的 3 个步骤数据
  const defaultItems = JSON.parse(JSON.stringify(MPTDefaultKeys))

  if (KeyType.value == MagnetSeniorType.SeniorKeyTypeMPT && Array.isArray(Keys.value) && Keys.value.length > 0) {
    // 确保始终填充到 3 个步骤，如果 Keys.value 长度不足，保留默认值
    const loadedData = JSON.parse(JSON.stringify(Keys.value))
    mptData.value = defaultItems.map((def: any, idx: number) => {
      return loadedData[idx] || def
    })
  } else {
    KeyType.value = MagnetSeniorType.SeniorKeyTypeMPT
    mptData.value = defaultItems
  }

  // 同步行程滑块数据
  mptData.value.forEach((item, index) => {
    if (travels.value[index] !== undefined) {
      travels.value[index] = item.Travel
    }
  })

  // 同步数据回全局，确保 Keys 长度至少为 3
  if (JSON.stringify(mptData.value) !== JSON.stringify(Keys.value)) {
    Keys.value = JSON.parse(JSON.stringify(mptData.value))
  }
}

watch(
  mptData,
  (val) => {
    if (JSON.stringify(val) !== JSON.stringify(Keys.value)) {
      Keys.value = JSON.parse(JSON.stringify(val))
    }
  },
  { deep: true },
)

onMounted(() => {
  initStep()
  initData()
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)
</script>

<style scoped lang="less">
.action_mpt_box {
  margin: 0 auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  padding: 4px 0;

  .mpt_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-m);
    padding: 12px 24px; /* 减小内边距 */
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    /* overflow: hidden; */

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
      border-radius: var(--radius-m); /* 手动同步父容器圆角 */
    }

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 24px;
      position: relative;
      z-index: 1;
    }

    .key_slot {
      width: 120px;
      height: 64px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 14px;
      border-radius: var(--radius-m);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 11px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 20px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      .flow_icon {
        width: 24px;
        height: 24px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }
      .flow_label {
        font-size: 9px;
        font-weight: 900;
        color: var(--brand-color-pure);
        letter-spacing: 2px;
        opacity: 0.7;
      }
    }

    .steps_preview_horizontal {
      display: flex;
      gap: 8px;
      .preview_dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--color-gray-1);
        transition: var(--transition-base);
        &.active {
          background: var(--brand-color-pure);
          box-shadow: 0 0 10px rgba(255, 124, 0, 0.4);
        }
      }
    }
  }

  .mpt_steps_grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    .mpt_step_card {
      background: var(--color-white);
      border-radius: var(--radius-m);
      padding: 12px 14px; /* 进一步压缩内边距 */
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08); /* 超淡投影 */
      border: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      gap: 12px;
      transition: var(--transition-base);
      min-height: 140px; /* 减小最小高度 */

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 15px 45px rgba(0, 0, 0, 0.12);
      }

      .card_content {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .key_slot.target {
          width: 100%;
          padding: 0;
          min-height: auto;
          background: none;
          box-shadow: none;
          border: none;

          &:hover {
            background: none;
            box-shadow: none;
            transform: none;
          }
          transform: translateY(-2px);
        }

        .slot_label {
          font-size: 9px;
          color: var(--color-text-hint);
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 2px;
          opacity: 0.6;
          letter-spacing: 0.5px;
        }

        .key_display {
          font-size: 16px;
          font-weight: 800;
          color: var(--color-dark);
          min-height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

          &.active {
            color: var(--brand-color-pure);
          }
        }
        &.empty .key_display {
          font-size: 12px;
          color: var(--color-text-hint);
          font-weight: 600;
        }

        .edit_hint {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 12px;
          height: 12px;
          color: var(--color-text-hint);
          opacity: 0.3;
        }
      }

      .step_slider_group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .slider_info {
          display: flex;
          justify-content: center;
          align-items: baseline;
          padding: 0 4px;

          .travel_display {
            font-size: 20px;
            font-weight: 900;
            color: var(--color-dark);
            span {
              font-size: 11px;
              margin-left: 2px;
              color: var(--color-text-hint);
              font-weight: 600;
            }
          }
        }

        .step_slider_container {
          padding: 4px 0;
          .slider_input {
            width: 100%;
            height: 6px; /* 统一高度 6px */
            -webkit-appearance: none;
            background: var(--color-gray-1);
            background-image: var(--brand-color);
            background-repeat: no-repeat;
            border-radius: 3px;
            outline: none;

            &::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 16px;
              height: 16px;
              background: #fff;
              border: 2px solid var(--brand-color-pure); /* 必须带有 2px 以上白色描边 - 这里是白色背景+品牌色边框符合语义 */
              border-radius: 50%;
              cursor: pointer;
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
              transition: transform 0.2s;

              &:hover {
                transform: scale(1.1);
              }
            }
          }
        }
      }
    }
  }
}
</style>
